import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { SearchBar } from '~components/Search/SearchBar';
import { useNavGroups } from '~pages/Settings/NavItems';
import { ROUTES as routes } from '~router/routePaths';

import { APPBAR_HEIGHT_IN_PX } from '../Dispatch/components/drivers/constants';

const drawerWidth = 240;

export const Settings = observer(() => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const navGroups = useNavGroups();
  const [searchQuery, setSearchQuery] = useState('');

  const settingsIndex = routes.settings.base;

  const navigateToSelectedPage = (link: string) => () => {
    navigate(`/${settingsIndex}/${link}`);
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const visibleGroups = navGroups
    .map((group) => {
      // A matching group label surfaces the whole group; otherwise filter items.
      const groupMatches = group.label.toLowerCase().includes(normalizedQuery);

      return {
        ...group,
        items: groupMatches
          ? group.items
          : group.items.filter((item) =>
              item.title.toLowerCase().includes(normalizedQuery),
            ),
      };
    })
    .filter((group) => group.items.length > 0);

  return (
    <Box
      sx={{
        display: 'flex',
        marginLeft: -5,
        marginRight: -5,
        marginTop: 0,
        flexGrow: 1,
        flexDirection: 'row',
        height: `calc(100vh - ${APPBAR_HEIGHT_IN_PX}px - var(--tread-agent-dock-height, 0px))`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: drawerWidth,
          minWidth: drawerWidth,
          flexShrink: 0,
          backgroundColor: theme.brandV2.colors.treadGray9,
          borderRight: `1px solid ${theme.palette.divider}`,
          overflow: 'auto',
        }}
      >
        <Box sx={{ px: 1, pt: 1.5, pb: 0.5 }}>
          <SearchBar
            alwaysExpanded
            dataUi="settings-nav-search"
            placeHolder={t('settings.nav_search_placeholder')}
            setFilter={setSearchQuery}
          />
        </Box>
        <List
          data-test-id="settings-nav-list"
          sx={{
            px: 1,
            pt: 0.5,
            '& .MuiListItemButton-root': {
              borderRadius: theme.brandV2.borderRadius,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
              },
              '&.Mui-selected': {
                backgroundColor: theme.brandV2.colors.treadGray8,
                '&:hover': {
                  backgroundColor: theme.brandV2.colors.treadGray8,
                },
              },
            },
          }}
        >
          {visibleGroups.map((group) => (
            <React.Fragment key={group.label}>
              <Typography
                variant="nav-group-title"
                sx={{ display: 'block', px: 1.5, pt: 2, pb: 0.5 }}
              >
                {group.label}
              </Typography>

              {group.items.map((navItem) => {
                const isActive =
                  location.pathname === `/${settingsIndex}/${navItem.link}`;

                return (
                  <ListItemButton
                    key={navItem.link}
                    selected={isActive}
                    onClick={navigateToSelectedPage(navItem.link)}
                    sx={{ minHeight: 32, py: 0.5 }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 1.25,
                        color: isActive
                          ? theme.brandV2.colors.treadBlack
                          : theme.brandV2.colors.treadGray4,
                        '& svg': { fontSize: 18 },
                      }}
                    >
                      {navItem.icon}
                    </ListItemIcon>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography
                          sx={{
                            fontSize: '13px',
                            fontWeight: isActive ? 500 : 400,
                            color: isActive
                              ? theme.brandV2.colors.treadBlack
                              : theme.brandV2.colors.treadGray2,
                          }}
                        >
                          {navItem.title}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </React.Fragment>
          ))}
        </List>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          flexShrink: 1,
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            py: 4,
            pl: 3,
            pr: 2,
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
});
